import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import QRCode from 'qrcode';

const { FiQrCode, FiPrinter, FiDownload, FiCopy, FiCheck } = FiIcons;

const QRGenerator = ({ onNavigate }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [checkInUrl, setCheckInUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    // Generate the URL for the check-in page
    const baseUrl = window.location.origin + window.location.pathname;
    // Use the /checkin route which is now properly handled in App.jsx
    const url = `${baseUrl}#/checkin`;
    setCheckInUrl(url);

    // Generate QR code
    QRCode.toCanvas(
      canvasRef.current,
      url,
      {
        width: 300,
        margin: 2,
        color: {
          dark: '#8C0E1B',
          light: '#FFFFFF'
        }
      },
      (error) => {
        if (error) console.error('QR Code generation failed:', error);
      }
    );

    // Also generate data URL for download
    QRCode.toDataURL(
      url,
      {
        width: 600,
        margin: 2,
        color: {
          dark: '#8C0E1B',
          light: '#FFFFFF'
        }
      },
      (error, url) => {
        if (error) console.error('QR Code generation failed:', error);
        else setQrCodeUrl(url);
      }
    );
  }, []);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(checkInUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const downloadQR = () => {
    if (!qrCodeUrl) return;
    const link = document.createElement('a');
    link.download = 'moco-png-checkin-qr.png';
    link.href = qrCodeUrl;
    link.click();
  };

  const printQR = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>MOCO PNG Check-In QR Code</title>
          <style>
            body {font-family: Arial, sans-serif; text-align: center; padding: 20px; margin: 0;}
            .header {color: #8C0E1B; margin-bottom: 20px;}
            .qr-container {margin: 20px 0;}
            .instructions {margin-top: 20px; font-size: 14px; color: #666;}
            @media print {body {margin: 0; padding: 10px;}}
          </style>
        </head>
        <body>
          <div class="header">
            <h1>MOCO PNG</h1>
            <h2>Member Check-In</h2>
            <p>Montgomery County Professional Networking Group</p>
          </div>
          <div class="qr-container">
            <img src="${qrCodeUrl}" alt="QR Code" style="max-width: 300px;" />
          </div>
          <div class="instructions">
            <p><strong>Instructions:</strong></p>
            <p>1. Scan this QR code with your phone camera</p>
            <p>2. Search for your name in the list</p>
            <p>3. Select your name and tap "Check In"</p>
            <p><strong>Meeting Time:</strong> 8:00 AM - 9:00 AM</p>
            <p><strong>Note:</strong> Check-ins after 8:05 AM are marked as late</p>
            <p><strong>Important:</strong> Check-ins are ONLY available on Tuesdays from 7:00 AM to 9:00 AM</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <h2 className="text-3xl font-bold text-gray-800">QR Code Generator</h2>
        <p className="text-gray-600 mt-2">
          Generate QR code for member self check-in
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="text-xl font-semibold flex items-center">
              <SafeIcon icon={FiQrCode} className="mr-3" />
              QR Code
            </h3>
          </div>
          <div className="card-body text-center">
            <div className="bg-white p-6 rounded-lg border-2 border-dashed border-gray-300 mb-6">
              <canvas ref={canvasRef} className="mx-auto" />
            </div>
            <div className="space-y-4">
              <button
                onClick={printQR}
                className="w-full btn-primary flex items-center justify-center"
              >
                <SafeIcon icon={FiPrinter} className="mr-2" />
                Print QR Code
              </button>
              <button
                onClick={downloadQR}
                className="w-full btn-secondary flex items-center justify-center"
              >
                <SafeIcon icon={FiDownload} className="mr-2" />
                Download QR Code
              </button>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="text-xl font-semibold">
              Check-In Instructions
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">For Members:</h4>
                <ol className="text-blue-700 space-y-1 text-sm">
                  <li>1. Scan the QR code with your phone camera</li>
                  <li>2. Search for your name in the list</li>
                  <li>3. Select your name and tap "Check In"</li>
                  <li>4. You'll see a confirmation message</li>
                </ol>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">Important Notes:</h4>
                <ul className="text-yellow-700 space-y-1 text-sm">
                  <li>• Meeting time: 8:00 AM - 9:00 AM</li>
                  <li>• Check-ins after 8:05 AM are marked as late</li>
                  <li>• Members can check in starting at 7:00 AM</li>
                  <li>• Check-ins ONLY available on Tuesdays from 7AM-9AM</li>
                  <li>• 3 consecutive absences = removal from active list</li>
                </ul>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Check-In URL:</h4>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={checkInUrl}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-sm"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="px-3 py-2 bg-brand-primary text-white rounded hover:bg-brand-secondary transition-colors"
                  >
                    {copied ? (
                      <SafeIcon icon={FiCheck} className="text-sm" />
                    ) : (
                      <SafeIcon icon={FiCopy} className="text-sm" />
                    )}
                  </button>
                </div>
                {copied && (
                  <p className="text-green-600 text-sm mt-1">URL copied to clipboard!</p>
                )}
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 mb-2">Setup Instructions:</h4>
                <ol className="text-red-700 space-y-1 text-sm">
                  <li>1. Print the QR code and place it at the entrance</li>
                  <li>2. Start the meeting in "Meeting Control" before 8:00 AM</li>
                  <li>3. Members will self check-in using their phones</li>
                  <li>4. End the meeting when finished to process absences</li>
                </ol>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default QRGenerator;